/**
 * Presets.js — the FX art direction, as data.
 * owner: fx
 *
 * Two tables:
 *   TYPES    — per-particle-type simulation + shading parameters, uploaded to the
 *              GPU as three small lookup textures (params / gradient / curve).
 *   RECIPES  — per-surface impact recipes: which bursts, which decal, which light.
 *
 * Colours are LINEAR and may exceed 1.0 — the post pipeline tonemaps.
 *
 * `soft` is the soft-particle depth-fade distance in METRES: a particle closer
 * than this to the geometry behind it is faded out to hide the intersection
 * seam. It has to be scaled to the effect. An impact puff is born 2-5 cm off the
 * wall it came out of, so a 0.26 m fade distance multiplies its opacity by ~0.1
 * and the burst silently disappears — which is exactly what it was doing. Wide
 * fades belong to big volumes (smoke columns, fire) that are metres from
 * anything; surface bursts get 0.10 or less.
 */
import * as THREE from 'three';
import { SPRITE, DECAL_TILE } from './Textures.js';

export const POOL = { ALPHA: 0, ADD: 1 };

/* ------------------------------------------------------------------ *
 * particle types
 * ------------------------------------------------------------------ *
 * grad: [t, r, g, b, a]  — colour (linear) and opacity over normalised life
 * size: [t, mult]        — size multiplier over normalised life
 */
const T = (o) => o;

export const TYPES = {
  /* ---------------- smoke & dust (lit, alpha, sorted) ---------------- */
  smoke_thick: T({
    pool: POOL.ALPHA, sprite: SPRITE.smoke, media: 1.0,
    gravity: -0.45, drag: 1.55, turb: 0.85, turbFreq: 0.30,
    stretch: 0.0, rotSpeed: 0.55, light: 1.0, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.85, dissolve: 0.70, sizeJitter: 0.62, tintJitter: 0.12,
    grad: [[0, 0.055, 0.052, 0.048, 0.0], [0.05, 0.075, 0.071, 0.066, 0.52],
           [0.35, 0.140, 0.135, 0.129, 0.46], [0.72, 0.215, 0.209, 0.201, 0.28],
           [1, 0.255, 0.249, 0.242, 0.0]],
    size: [[0, 0.45], [0.25, 1.15], [1, 2.45]],
  }),
  smoke_muzzle: T({
    pool: POOL.ALPHA, sprite: SPRITE.wisp, media: 0.75,
    gravity: -0.75, drag: 2.4, turb: 1.35, turbFreq: 0.7,
    stretch: 0.25, rotSpeed: 1.4, light: 1.0, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.35, dissolve: 1.25, sizeJitter: 0.6, tintJitter: 0.12,
    grad: [[0, 0.185, 0.180, 0.174, 0.0], [0.10, 0.245, 0.239, 0.233, 0.30],
           [0.45, 0.285, 0.279, 0.273, 0.19], [1, 0.310, 0.306, 0.302, 0.0]],
    size: [[0, 0.45], [0.35, 1.6], [1, 3.4]],
  }),
  smoke_trail: T({
    pool: POOL.ALPHA, sprite: SPRITE.mist, media: 0.6,
    gravity: -0.25, drag: 2.0, turb: 0.5, turbFreq: 0.6,
    stretch: 0.0, rotSpeed: 0.8, light: 1.0, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.3, dissolve: 1.3, sizeJitter: 0.5, tintJitter: 0.1,
    grad: [[0, 0.205, 0.199, 0.193, 0.0], [0.15, 0.255, 0.249, 0.243, 0.16], [1, 0.28, 0.28, 0.28, 0.0]],
    size: [[0, 0.5], [1, 2.4]],
  }),
  dust_concrete: T({
    pool: POOL.ALPHA, sprite: SPRITE.dust, media: 0.38,
    gravity: 0.65, drag: 2.5, turb: 0.85, turbFreq: 1.15,
    stretch: 0.10, rotSpeed: 2.1, light: 1.0, bounce: 0.15, collide: 1,
    emissive: 0.0, soft: 0.10, dissolve: 0.52, sizeJitter: 1.05, tintJitter: 0.22,
    grad: [[0, 0.511, 0.500, 0.471, 0.0], [0.05, 0.611, 0.596, 0.564, 0.60],
           [0.45, 0.585, 0.572, 0.542, 0.40], [1, 0.562, 0.551, 0.524, 0.0]],
    size: [[0, 0.5], [0.3, 1.3], [1, 2.3]],
  }),
  dust_linger: T({
    pool: POOL.ALPHA, sprite: SPRITE.smoke, media: 0.42,
    gravity: 0.16, drag: 2.3, turb: 0.85, turbFreq: 0.35,
    stretch: 0.0, rotSpeed: 0.35, light: 1.0, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.16, dissolve: 0.7, sizeJitter: 0.9, tintJitter: 0.18,
    grad: [[0, 0.490, 0.479, 0.452, 0.0], [0.12, 0.585, 0.574, 0.545, 0.38],
           [0.55, 0.574, 0.564, 0.538, 0.24], [1, 0.562, 0.552, 0.528, 0.0]],
    size: [[0, 0.7], [0.4, 1.75], [1, 3.1]],
  }),
  dust_dirt: T({
    pool: POOL.ALPHA, sprite: SPRITE.dust, media: 0.42,
    gravity: 1.6, drag: 2.6, turb: 0.45, turbFreq: 0.8,
    stretch: 0.15, rotSpeed: 1.3, light: 1.0, bounce: 0.2, collide: 1,
    emissive: 0.0, soft: 0.10, dissolve: 1.05, sizeJitter: 0.75, tintJitter: 0.18,
    grad: [[0, 0.241, 0.185, 0.125, 0.0], [0.07, 0.298, 0.229, 0.153, 0.72],
           [0.55, 0.273, 0.210, 0.142, 0.46], [1, 0.254, 0.203, 0.141, 0.0]],
    size: [[0, 0.45], [0.35, 1.6], [1, 2.6]],
  }),
  dust_sand: T({
    pool: POOL.ALPHA, sprite: SPRITE.dust, media: 0.42,
    gravity: 1.1, drag: 2.9, turb: 0.55, turbFreq: 0.85,
    stretch: 0.12, rotSpeed: 1.2, light: 1.0, bounce: 0.18, collide: 1,
    emissive: 0.0, soft: 0.10, dissolve: 1.05, sizeJitter: 0.7, tintJitter: 0.16,
    grad: [[0, 0.667, 0.542, 0.359, 0.0], [0.07, 0.741, 0.609, 0.412, 0.72],
           [0.55, 0.716, 0.585, 0.395, 0.46], [1, 0.693, 0.574, 0.382, 0.0]],
    size: [[0, 0.45], [0.35, 1.7], [1, 2.8]],
  }),
  dust_wood: T({
    pool: POOL.ALPHA, sprite: SPRITE.dust, media: 0.38,
    gravity: 0.5, drag: 3.6, turb: 0.5, turbFreq: 1.0,
    stretch: 0.1, rotSpeed: 1.7, light: 1.0, bounce: 0.1, collide: 1,
    emissive: 0.0, soft: 0.10, dissolve: 0.75, sizeJitter: 1.0, tintJitter: 0.28,
    grad: [[0, 0.364, 0.273, 0.168, 0.0], [0.05, 0.449, 0.344, 0.214, 0.50],
           [0.5, 0.425, 0.327, 0.204, 0.31], [1, 0.412, 0.317, 0.196, 0.0]],
    size: [[0, 0.4], [0.3, 1.4], [1, 2.4]],
  }),
  dust_foot: T({
    pool: POOL.ALPHA, sprite: SPRITE.dust, media: 0.34,
    gravity: 0.2, drag: 3.2, turb: 0.7, turbFreq: 0.9,
    stretch: 0.0, rotSpeed: 1.0, light: 1.0, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.10, dissolve: 1.1, sizeJitter: 0.7, tintJitter: 0.14,
    grad: [[0, 0.618, 0.595, 0.550, 0.0], [0.12, 0.673, 0.656, 0.618, 0.20],
           [1, 0.650, 0.640, 0.605, 0.0]],
    size: [[0, 0.5], [1, 2.6]],
  }),

  /* ---------------- solid debris (lit, alpha, collides) ---------------- */
  chip_concrete: T({
    pool: POOL.ALPHA, sprite: SPRITE.chip, media: 0,
    gravity: 9.0, drag: 0.35, turb: 0.10, turbFreq: 1.6,
    stretch: 0.06, rotSpeed: 9.0, light: 1.0, bounce: 0.34, collide: 1,
    emissive: 0.0, soft: 0.05, dissolve: 0.0, sizeJitter: 1.0, tintJitter: 0.42,
    grad: [[0, 0.175, 0.170, 0.162, 1.0], [0.85, 0.175, 0.170, 0.162, 1.0], [1, 0.175, 0.170, 0.162, 0.0]],
    size: [[0, 1], [1, 1]],
  }),
  splinter_wood: T({
    pool: POOL.ALPHA, sprite: SPRITE.splinter, media: 0,
    gravity: 8.2, drag: 0.9, turb: 0.25, turbFreq: 1.4,
    stretch: 0.10, rotSpeed: 13.0, light: 1.0, bounce: 0.26, collide: 1,
    emissive: 0.0, soft: 0.05, dissolve: 0.0, sizeJitter: 0.9, tintJitter: 0.32,
    grad: [[0, 0.205, 0.142, 0.081, 1.0], [0.85, 0.205, 0.142, 0.081, 1.0], [1, 0.205, 0.142, 0.081, 0.0]],
    size: [[0, 1], [1, 1]],
  }),
  shard_glass: T({
    pool: POOL.ALPHA, sprite: SPRITE.shard, media: 0,
    gravity: 8.6, drag: 0.6, turb: 0.15, turbFreq: 1.5,
    stretch: 0.05, rotSpeed: 11.0, light: 1.6, bounce: 0.30, collide: 1,
    emissive: 0.9, soft: 0.05, dissolve: 0.0, sizeJitter: 0.9, tintJitter: 0.12,
    grad: [[0, 0.70, 0.80, 0.86, 0.85], [0.85, 0.70, 0.80, 0.86, 0.8], [1, 0.7, 0.8, 0.86, 0.0]],
    size: [[0, 1], [1, 1]],
  }),
  debris_dark: T({
    pool: POOL.ALPHA, sprite: SPRITE.chip, media: 0,
    gravity: 9.4, drag: 0.30, turb: 0.12, turbFreq: 1.2,
    stretch: 0.06, rotSpeed: 7.0, light: 1.0, bounce: 0.28, collide: 1,
    emissive: 0.0, soft: 0.05, dissolve: 0.0, sizeJitter: 0.95, tintJitter: 0.35,
    grad: [[0, 0.082, 0.074, 0.066, 1.0], [0.88, 0.082, 0.074, 0.066, 1.0], [1, 0.082, 0.074, 0.066, 0.0]],
    size: [[0, 1], [1, 1]],
  }),
  leaf_frag: T({
    pool: POOL.ALPHA, sprite: SPRITE.leaf, media: 0,
    gravity: 2.2, drag: 2.4, turb: 1.5, turbFreq: 1.1,
    stretch: 0.0, rotSpeed: 6.0, light: 1.3, bounce: 0.1, collide: 1,
    emissive: 0.0, soft: 0.05, dissolve: 0.0, sizeJitter: 0.8, tintJitter: 0.45,
    grad: [[0, 0.115, 0.175, 0.055, 1.0], [0.85, 0.115, 0.175, 0.055, 1.0], [1, 0.115, 0.175, 0.055, 0.0]],
    size: [[0, 1], [1, 1]],
  }),

  /* ---------------- liquid ---------------- */
  droplet_water: T({
    pool: POOL.ALPHA, sprite: SPRITE.droplet, media: 0,
    gravity: 9.2, drag: 0.9, turb: 0.15, turbFreq: 1.4,
    stretch: 0.55, rotSpeed: 0.0, light: 1.8, bounce: 0.0, collide: 1,
    emissive: 0.55, soft: 0.05, dissolve: 0.0, sizeJitter: 0.9, tintJitter: 0.08,
    grad: [[0, 0.55, 0.62, 0.68, 0.75], [0.8, 0.55, 0.62, 0.68, 0.6], [1, 0.55, 0.62, 0.68, 0.0]],
    size: [[0, 1], [1, 0.9]],
  }),
  mist_water: T({
    pool: POOL.ALPHA, sprite: SPRITE.mist, media: 0.4,
    gravity: 0.6, drag: 3.2, turb: 0.6, turbFreq: 0.9,
    stretch: 0.0, rotSpeed: 0.8, light: 1.5, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.2, dissolve: 1.1, sizeJitter: 0.7, tintJitter: 0.06,
    grad: [[0, 0.36, 0.395, 0.418, 0.0], [0.12, 0.395, 0.430, 0.453, 0.26], [1, 0.395, 0.430, 0.453, 0.0]],
    size: [[0, 0.6], [1, 2.6]],
  }),
  blood_drop: T({
    pool: POOL.ALPHA, sprite: SPRITE.blood, media: 0,
    gravity: 9.0, drag: 0.7, turb: 0.10, turbFreq: 1.3,
    stretch: 0.75, rotSpeed: 0.0, light: 1.0, bounce: 0.0, collide: 1,
    emissive: 0.0, soft: 0.05, dissolve: 0.0, sizeJitter: 0.9, tintJitter: 0.22,
    grad: [[0, 0.155, 0.010, 0.008, 1.0], [0.85, 0.155, 0.010, 0.008, 1.0], [1, 0.155, 0.01, 0.008, 0.0]],
    size: [[0, 1], [1, 0.95]],
  }),
  blood_mist: T({
    pool: POOL.ALPHA, sprite: SPRITE.mist, media: 0.5,
    gravity: 1.6, drag: 3.0, turb: 0.55, turbFreq: 1.0,
    stretch: 0.35, rotSpeed: 0.9, light: 1.1, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.09, dissolve: 1.2, sizeJitter: 0.75, tintJitter: 0.2,
    grad: [[0, 0.150, 0.012, 0.008, 0.0], [0.06, 0.196, 0.016, 0.011, 0.44],
           [0.45, 0.162, 0.013, 0.009, 0.25], [1, 0.133, 0.011, 0.008, 0.0]],
    size: [[0, 0.5], [0.4, 1.7], [1, 2.6]],
  }),

  /* ---------------- ambience ---------------- */
  mote: T({
    pool: POOL.ALPHA, sprite: SPRITE.sparkPoint, media: 0,
    gravity: -0.02, drag: 0.8, turb: 0.30, turbFreq: 0.55,
    stretch: 0.0, rotSpeed: 0.2, light: 2.6, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.12, dissolve: 0.0, sizeJitter: 0.85, tintJitter: 0.2,
    grad: [[0, 0.85, 0.82, 0.74, 0.0], [0.15, 0.9, 0.87, 0.78, 0.55],
           [0.8, 0.9, 0.87, 0.78, 0.45], [1, 0.9, 0.87, 0.78, 0.0]],
    size: [[0, 0.8], [0.5, 1.1], [1, 0.9]],
  }),
  grit: T({
    pool: POOL.ALPHA, sprite: SPRITE.chip, media: 0,
    gravity: 0.9, drag: 1.4, turb: 2.2, turbFreq: 0.7,
    stretch: 0.35, rotSpeed: 5.0, light: 1.4, bounce: 0.1, collide: 0,
    emissive: 0.0, soft: 0.08, dissolve: 0.0, sizeJitter: 0.9, tintJitter: 0.3,
    grad: [[0, 0.44, 0.40, 0.34, 0.0], [0.12, 0.5, 0.46, 0.39, 0.7],
           [0.85, 0.5, 0.46, 0.39, 0.5], [1, 0.5, 0.46, 0.39, 0.0]],
    size: [[0, 1], [1, 1]],
  }),
  haze: T({
    pool: POOL.ALPHA, sprite: SPRITE.mist, media: 0.32,
    gravity: -0.35, drag: 1.2, turb: 1.2, turbFreq: 1.4,
    stretch: 0.0, rotSpeed: 0.4, light: 1.2, bounce: 0.0, collide: 0,
    emissive: 0.0, soft: 0.5, dissolve: 1.0, sizeJitter: 0.6, tintJitter: 0.05,
    grad: [[0, 0.29, 0.285, 0.273, 0.0], [0.25, 0.32, 0.314, 0.302, 0.055], [1, 0.32, 0.314, 0.302, 0.0]],
    size: [[0, 0.8], [1, 2.2]],
  }),

  /* ---------------- additive: sparks, fire, glints ---------------- */
  spark_hot: T({
    pool: POOL.ADD, sprite: SPRITE.sparkStreak,
    gravity: 8.4, drag: 2.6, turb: 0.85, turbFreq: 1.8,
    stretch: 5.0, rotSpeed: 0.0, light: 0.0, bounce: 0.42, collide: 1,
    emissive: 1.0, soft: 0.06, dissolve: 0.0, sizeJitter: 0.75, tintJitter: 0.28,
    grad: [[0, 22.0, 13.0, 4.6, 1.0], [0.10, 14.0, 5.6, 1.05, 1.0],
           [0.35, 6.2, 1.55, 0.16, 0.95], [0.68, 2.1, 0.36, 0.03, 0.7],
           [0.9, 0.55, 0.07, 0.006, 0.35], [1, 0.1, 0.01, 0.0, 0.0]],
    size: [[0, 1.0], [0.5, 0.8], [1, 0.25]],
  }),
  spark_ricochet: T({
    pool: POOL.ADD, sprite: SPRITE.sparkStreak,
    gravity: 8.6, drag: 1.35, turb: 0.35, turbFreq: 1.4,
    stretch: 8.0, rotSpeed: 0.0, light: 0.0, bounce: 0.5, collide: 1,
    emissive: 1.0, soft: 0.06, dissolve: 0.0, sizeJitter: 0.6, tintJitter: 0.2,
    grad: [[0, 26.0, 15.0, 5.5, 1.0], [0.15, 12.0, 4.4, 0.75, 1.0],
           [0.45, 4.4, 0.95, 0.09, 0.9], [0.8, 1.0, 0.14, 0.01, 0.5], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 1.0], [1, 0.35]],
  }),
  spark_small: T({
    pool: POOL.ADD, sprite: SPRITE.sparkPoint,
    gravity: 6.8, drag: 3.4, turb: 1.1, turbFreq: 2.2,
    stretch: 2.6, rotSpeed: 0.0, light: 0.0, bounce: 0.35, collide: 1,
    emissive: 1.0, soft: 0.06, dissolve: 0.0, sizeJitter: 0.8, tintJitter: 0.3,
    grad: [[0, 16.0, 8.0, 2.4, 1.0], [0.25, 6.0, 1.7, 0.18, 0.95],
           [0.6, 1.6, 0.26, 0.02, 0.6], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 1.0], [1, 0.3]],
  }),
  ember: T({
    pool: POOL.ADD, sprite: SPRITE.ember,
    gravity: -0.35, drag: 1.5, turb: 1.9, turbFreq: 0.9,
    stretch: 0.6, rotSpeed: 2.0, light: 0.0, bounce: 0.2, collide: 0,
    emissive: 1.0, soft: 0.1, dissolve: 0.0, sizeJitter: 0.85, tintJitter: 0.35,
    grad: [[0, 5.2, 1.55, 0.18, 0.0], [0.08, 4.4, 1.20, 0.13, 1.0],
           [0.5, 2.0, 0.42, 0.035, 0.85], [0.85, 0.6, 0.09, 0.006, 0.45], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 0.8], [0.4, 1.0], [1, 0.5]],
  }),
  fire_core: T({
    pool: POOL.ADD, sprite: SPRITE.fire,
    gravity: -2.6, drag: 2.4, turb: 1.6, turbFreq: 0.7,
    stretch: 0.25, rotSpeed: 1.1, light: 0.0, bounce: 0.0, collide: 0,
    emissive: 1.0, soft: 0.6, dissolve: 0.85, sizeJitter: 0.62, tintJitter: 0.18,
    grad: [[0, 16.0, 9.5, 3.4, 0.0], [0.06, 13.0, 6.4, 1.6, 1.0],
           [0.28, 6.6, 2.10, 0.28, 0.9], [0.55, 2.0, 0.42, 0.035, 0.6],
           [0.8, 0.45, 0.06, 0.004, 0.25], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 0.45], [0.35, 1.5], [1, 2.5]],
  }),
  fire_edge: T({
    pool: POOL.ADD, sprite: SPRITE.fire,
    gravity: -3.4, drag: 1.9, turb: 2.3, turbFreq: 1.0,
    stretch: 0.35, rotSpeed: 1.7, light: 0.0, bounce: 0.0, collide: 0,
    emissive: 1.0, soft: 0.5, dissolve: 1.0, sizeJitter: 0.8, tintJitter: 0.28,
    grad: [[0, 9.0, 3.6, 0.7, 0.0], [0.10, 6.4, 1.9, 0.22, 0.9],
           [0.4, 2.4, 0.5, 0.04, 0.6], [0.75, 0.5, 0.07, 0.005, 0.25], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 0.5], [0.4, 1.8], [1, 3.0]],
  }),
  glint: T({
    pool: POOL.ADD, sprite: SPRITE.star,
    gravity: 8.6, drag: 0.6, turb: 0.1, turbFreq: 1.0,
    stretch: 0.0, rotSpeed: 3.0, light: 0.0, bounce: 0.3, collide: 1,
    emissive: 1.0, soft: 0.06, dissolve: 0.0, sizeJitter: 0.9, tintJitter: 0.12,
    grad: [[0, 3.6, 4.2, 5.0, 0.0], [0.1, 3.0, 3.5, 4.2, 0.9],
           [0.55, 0.9, 1.1, 1.4, 0.5], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 0.7], [0.3, 1.15], [1, 0.55]],
  }),
  flash_core: T({
    pool: POOL.ADD, sprite: SPRITE.star,
    gravity: 0.0, drag: 8.0, turb: 0.0, turbFreq: 1.0,
    stretch: 0.0, rotSpeed: 0.0, light: 0.0, bounce: 0.0, collide: 0,
    emissive: 1.0, soft: 0.25, dissolve: 0.0, sizeJitter: 0.35, tintJitter: 0.08,
    grad: [[0, 22.0, 15.0, 7.6, 1.0], [0.35, 12.0, 6.4, 2.1, 0.85],
           [0.7, 3.4, 1.2, 0.22, 0.4], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 0.6], [0.25, 1.15], [1, 1.35]],
  }),
  gas_puff: T({
    pool: POOL.ADD, sprite: SPRITE.fire,
    gravity: -0.6, drag: 9.0, turb: 1.0, turbFreq: 1.6,
    stretch: 0.4, rotSpeed: 2.4, light: 0.0, bounce: 0.0, collide: 0,
    emissive: 1.0, soft: 0.2, dissolve: 1.6, sizeJitter: 0.55, tintJitter: 0.18,
    grad: [[0, 14.0, 7.2, 2.4, 0.0], [0.12, 8.0, 3.2, 0.65, 0.95],
           [0.42, 2.4, 0.62, 0.07, 0.55], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 0.5], [0.4, 1.7], [1, 2.6]],
  }),
  shock_ring: T({
    pool: POOL.ADD, sprite: SPRITE.ring,
    gravity: 0.0, drag: 5.5, turb: 0.0, turbFreq: 1.0,
    stretch: 0.0, rotSpeed: 0.2, light: 0.0, bounce: 0.0, collide: 0,
    emissive: 1.0, soft: 0.5, dissolve: 0.0, sizeJitter: 0.05, tintJitter: 0.02,
    grad: [[0, 7.0, 5.4, 3.6, 0.0], [0.10, 4.4, 3.2, 2.0, 0.8],
           [0.45, 1.2, 0.8, 0.45, 0.35], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 0.35], [0.5, 2.6], [1, 4.4]],
  }),
  tracer_glow: T({
    pool: POOL.ADD, sprite: SPRITE.sparkPoint,
    gravity: 0.0, drag: 0.0, turb: 0.0, turbFreq: 1.0,
    stretch: 0.0, rotSpeed: 0.0, light: 0.0, bounce: 0.0, collide: 0,
    emissive: 1.0, soft: 0.1, dissolve: 0.0, sizeJitter: 0.2, tintJitter: 0.1,
    grad: [[0, 9.0, 4.5, 1.2, 1.0], [0.5, 4.0, 1.6, 0.3, 0.7], [1, 0.0, 0.0, 0.0, 0.0]],
    size: [[0, 1.0], [1, 0.4]],
  }),
};

export const TYPE_NAMES = Object.keys(TYPES);
export const TYPE_INDEX = Object.fromEntries(TYPE_NAMES.map((n, i) => [n, i]));

/* ------------------------------------------------------------------ *
 * lookup-texture construction
 * ------------------------------------------------------------------ */
const GRAD_W = 64;
const PARAM_COLS = 4;

function sampleStops(stops, t, comps) {
  // stops: [[t, ...comps]]
  let i = 0;
  while (i < stops.length - 1 && stops[i + 1][0] < t) i++;
  const a = stops[Math.min(i, stops.length - 1)];
  const b = stops[Math.min(i + 1, stops.length - 1)];
  const span = Math.max(1e-5, b[0] - a[0]);
  const f = Math.max(0, Math.min(1, (t - a[0]) / span));
  const out = [];
  for (let c = 1; c <= comps; c++) out.push(a[c] + (b[c] - a[c]) * f);
  return out;
}

export function buildTypeTextures() {
  const n = TYPE_NAMES.length;

  const grad = new Float32Array(GRAD_W * n * 4);
  const curve = new Float32Array(GRAD_W * n * 4);
  for (let ti = 0; ti < n; ti++) {
    const def = TYPES[TYPE_NAMES[ti]];
    for (let x = 0; x < GRAD_W; x++) {
      const t = x / (GRAD_W - 1);
      const c = sampleStops(def.grad, t, 4);
      const o = (ti * GRAD_W + x) * 4;
      grad[o] = c[0]; grad[o + 1] = c[1]; grad[o + 2] = c[2]; grad[o + 3] = c[3];
      const s = sampleStops(def.size, t, 1);
      curve[o] = s[0]; curve[o + 1] = 1; curve[o + 2] = 1; curve[o + 3] = 1;
    }
  }

  const params = new Float32Array(PARAM_COLS * n * 4);
  for (let ti = 0; ti < n; ti++) {
    const d = TYPES[TYPE_NAMES[ti]];
    const o = ti * PARAM_COLS * 4;
    params[o + 0] = d.gravity; params[o + 1] = d.drag; params[o + 2] = d.turb; params[o + 3] = d.turbFreq;
    params[o + 4] = d.stretch; params[o + 5] = d.rotSpeed; params[o + 6] = d.light; params[o + 7] = d.bounce;
    params[o + 8] = d.emissive; params[o + 9] = d.soft; params[o + 10] = d.dissolve; params[o + 11] = d.sprite;
    params[o + 12] = d.sizeJitter; params[o + 13] = d.tintJitter; params[o + 14] = d.collide;
    // media > 0 -> shade as participating media (phase function + optical depth
    // toward the light), and the VALUE is the optical density of that medium:
    // 1.0 is a dense smoke column, 0.5 a thin dust powder. media = 0 -> shade as
    // a small solid (wrap diffuse). Shading smoke as a surface is what turns
    // every puff into a lit disc; shading dust as dense smoke turns a sunlit
    // impact puff into a dark smudge.
    params[o + 15] = d.media ?? 0;
  }

  const mk = (data, w, h, filter) => {
    const t = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.FloatType);
    t.minFilter = filter; t.magFilter = filter;
    t.wrapS = THREE.ClampToEdgeWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
    t.generateMipmaps = false; t.colorSpace = THREE.NoColorSpace;
    t.needsUpdate = true;
    return t;
  };

  return {
    gradient: mk(grad, GRAD_W, n, THREE.LinearFilter),
    curve: mk(curve, GRAD_W, n, THREE.LinearFilter),
    params: mk(params, PARAM_COLS, n, THREE.NearestFilter),
    count: n,
    cols: PARAM_COLS,
  };
}

/* ------------------------------------------------------------------ *
 * surface impact recipes
 * ------------------------------------------------------------------ *
 * axis:  'n'    hemisphere around the surface normal
 *        'r'    ricochet — reflect the incoming direction about the normal
 *        'nr'   halfway between the two
 *        'up'   world up
 */
const B = (type, count, o = {}) => ({
  type, count,
  speed: o.speed ?? [1, 3],
  spread: o.spread ?? 0.9,
  axis: o.axis ?? 'n',
  life: o.life ?? [0.6, 1.2],
  size: o.size ?? [0.05, 0.12],
  offset: o.offset ?? 0.02,
  radius: o.radius ?? 0.02,
  tangent: o.tangent ?? 0,        // extra push along the surface plane
  inherit: o.inherit ?? 0,
});

export const RECIPES = {
  concrete: {
    bursts: [
      B('dust_concrete', 190, { speed: [1.1, 4.8], spread: 0.72, life: [0.34, 0.95], size: [0.015, 0.058], radius: 0.035, offset: 0.045 }),
      B('dust_linger', 34, { speed: [0.18, 0.9], spread: 1.4, life: [1.3, 2.7], size: [0.042, 0.115], radius: 0.07, offset: 0.05 }),
      B('chip_concrete', 26, { speed: [2.2, 7.5], spread: 0.9, axis: 'nr', life: [0.9, 1.9], size: [0.006, 0.020] }),
      B('spark_small', 4, { speed: [2.5, 6.0], spread: 1.0, axis: 'r', life: [0.10, 0.26], size: [0.010, 0.022] }),
      B('flash_core', 1, { speed: [0, 0], spread: 0.1, life: [0.075, 0.08], size: [0.075, 0.08], offset: 0.02 }),
    ],
    decal: { tile: DECAL_TILE.holeConcrete, size: [0.19, 0.28], tint: [1.16, 1.14, 1.09], depth: 0.11, life: 55 },
    light: { color: [1.0, 0.85, 0.62], intensity: 4.0, radius: 2.6, time: 0.09 },
    audio: 'concrete',
  },
  metal: {
    bursts: [
      B('spark_hot', 52, { speed: [2.6, 13.0], spread: 0.95, axis: 'r', life: [0.16, 0.52], size: [0.014, 0.042] }),
      B('spark_ricochet', 6, { speed: [11.0, 21.0], spread: 0.40, axis: 'r', life: [0.26, 0.62], size: [0.020, 0.040] }),
      B('spark_small', 40, { speed: [1.2, 6.5], spread: 1.45, axis: 'nr', life: [0.09, 0.34], size: [0.007, 0.020] }),
      B('flash_core', 1, { speed: [0, 0], spread: 0.1, life: [0.11, 0.12], size: [0.135, 0.14], offset: 0.02 }),
      B('smoke_trail', 5, { speed: [0.3, 1.4], spread: 1.2, life: [0.45, 1.0], size: [0.028, 0.075] }),
    ],
    decal: { tile: DECAL_TILE.holeMetal, size: [0.12, 0.18], tint: [1.11, 1.08, 1.04], depth: 0.08, life: 55 },
    light: { color: [1.0, 0.76, 0.40], intensity: 18.0, radius: 3.8, time: 0.14 },
    audio: 'metal',
  },
  wood: {
    bursts: [
      B('splinter_wood', 24, { speed: [2.2, 8.0], spread: 0.85, axis: 'nr', life: [0.9, 1.9], size: [0.010, 0.048] }),
      B('dust_wood', 130, { speed: [0.9, 3.6], spread: 0.85, life: [0.28, 0.72], size: [0.010, 0.038], offset: 0.045 }),
      B('dust_linger', 14, { speed: [0.2, 0.85], spread: 1.35, life: [1.0, 2.1], size: [0.035, 0.09], offset: 0.04 }),
    ],
    decal: { tile: DECAL_TILE.holeWood, size: [0.15, 0.23], tint: [1.13, 1.02, 0.86], depth: 0.09, life: 55 },
    light: null,
    audio: 'wood',
  },
  dirt: {
    bursts: [
      B('dust_dirt', 120, { speed: [1.2, 4.8], spread: 0.75, life: [0.5, 1.15], size: [0.018, 0.075], radius: 0.055, offset: 0.05 }),
      B('dust_linger', 5, { speed: [0.2, 1.0], spread: 1.4, life: [1.2, 2.4], size: [0.10, 0.24], offset: 0.05 }),
      B('debris_dark', 12, { speed: [3.0, 8.5], spread: 0.6, axis: 'n', life: [0.9, 1.8], size: [0.008, 0.026] }),
    ],
    decal: { tile: DECAL_TILE.holeDirt, size: [0.22, 0.33], tint: [0.70, 0.61, 0.50], depth: 0.10, life: 45 },
    light: null,
    audio: 'dirt',
  },
  sand: {
    bursts: [
      B('dust_sand', 130, { speed: [1.1, 4.4], spread: 0.8, life: [0.55, 1.25], size: [0.018, 0.080], radius: 0.055, offset: 0.05 }),
      B('dust_linger', 6, { speed: [0.2, 1.0], spread: 1.45, life: [1.4, 2.8], size: [0.11, 0.26], offset: 0.05 }),
      B('grit', 14, { speed: [2.5, 7.0], spread: 0.65, life: [0.8, 1.5], size: [0.006, 0.016] }),
    ],
    decal: { tile: DECAL_TILE.sandCrater, size: [0.24, 0.36], tint: [1.10, 1.04, 0.94], depth: 0.08, life: 45 },
    light: null,
    audio: 'sand',
  },
  glass: {
    bursts: [
      B('shard_glass', 22, { speed: [2.5, 8.0], spread: 1.0, axis: 'nr', life: [1.0, 2.0], size: [0.015, 0.055] }),
      B('glint', 10, { speed: [2.0, 7.0], spread: 1.2, axis: 'nr', life: [0.4, 0.9], size: [0.03, 0.075] }),
      B('dust_concrete', 6, { speed: [0.8, 2.6], spread: 1.1, life: [0.3, 0.7], size: [0.02, 0.06] }),
    ],
    decal: { tile: DECAL_TILE.glassShatter, size: [0.36, 0.55], tint: [1.35, 1.45, 1.55], depth: 0.03, life: 120 },
    light: { color: [0.75, 0.88, 1.0], intensity: 2.0, radius: 2.0, time: 0.05 },
    audio: 'glass',
  },
  fabric: {
    bursts: [
      B('dust_wood', 10, { speed: [1.0, 3.0], spread: 0.9, life: [0.4, 0.9], size: [0.03, 0.09] }),
      B('debris_dark', 6, { speed: [1.5, 4.5], spread: 0.9, life: [0.7, 1.4], size: [0.006, 0.018] }),
    ],
    decal: { tile: DECAL_TILE.dustSmudge, size: [0.12, 0.19], tint: [0.72, 0.68, 0.62], depth: 0.02, life: 60 },
    light: null,
    audio: 'fabric',
  },
  water: {
    bursts: [
      B('droplet_water', 30, { speed: [2.5, 7.5], spread: 0.45, axis: 'up', life: [0.5, 1.1], size: [0.010, 0.030] }),
      B('mist_water', 10, { speed: [0.8, 2.6], spread: 1.0, axis: 'up', life: [0.5, 1.1], size: [0.05, 0.14] }),
      B('glint', 6, { speed: [1.5, 5.0], spread: 0.7, axis: 'up', life: [0.3, 0.7], size: [0.02, 0.045] }),
    ],
    decal: { tile: DECAL_TILE.waterRipple, size: [0.5, 0.8], tint: [0.85, 0.95, 1.05], depth: 0.02, life: 3 },
    light: null,
    audio: 'water',
  },
  flesh: {
    bursts: [
      B('blood_mist', 56, { speed: [0.8, 3.8], spread: 0.9, life: [0.28, 0.70], size: [0.012, 0.055], radius: 0.035 }),
      B('blood_drop', 40, { speed: [1.8, 8.0], spread: 1.0, axis: 'nr', life: [0.7, 1.5], size: [0.006, 0.024] }),
      B('blood_mist', 10, { speed: [0.4, 1.8], spread: 1.4, axis: 'r', life: [0.5, 1.2], size: [0.035, 0.105] }),
    ],
    decal: { tile: DECAL_TILE.bloodA, size: [0.32, 0.58], tint: [0.34, 0.045, 0.030], depth: 0.02, life: 120 },
    light: null,
    audio: 'flesh',
  },
  foliage: {
    bursts: [
      B('leaf_frag', 16, { speed: [1.5, 5.0], spread: 1.1, axis: 'nr', life: [1.2, 2.6], size: [0.02, 0.055] }),
      B('dust_wood', 6, { speed: [0.8, 2.4], spread: 1.2, life: [0.4, 0.9], size: [0.03, 0.08] }),
    ],
    decal: { tile: DECAL_TILE.foliage, size: [0.14, 0.22], tint: [0.42, 0.62, 0.24], depth: 0.02, life: 45 },
    light: null,
    audio: 'foliage',
  },
};

export const DEFAULT_SURFACE = 'concrete';
